import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Shield, Camera, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerificationSection() {
  const { user, token, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [verified, setVerified] = useState(user?.verified || false);

  const handleVerify = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(`${API}/profile/verify`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (data.verified) {
        setVerified(true);
        updateUser({ ...user, verified: true });
      }
    } catch (e) {
      console.error('Verification failed:', e);
    } finally {
      setUploading(false);
    }
  };

  if (verified) {
    return (
      <div className="p-4 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center gap-3">
        <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
        <div>
          <h4 className="font-medium text-green-400 text-sm">Profile Verified</h4>
          <p className="text-zinc-500 text-xs">Your identity has been confirmed</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 rounded-2xl bg-zinc-900/50 border border-zinc-800">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-5 h-5 text-blue-400" />
        <h4 className="font-outfit font-medium text-white">Get Verified</h4>
      </div>
      <p className="text-zinc-500 text-sm mb-4">
        Take a selfie to verify your identity. Verified profiles get more matches!
      </p>
      <Button
        data-testid="verify-profile-btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        variant="outline"
        className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10 rounded-full"
      >
        {uploading ? (
          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Verifying...</>
        ) : (
          <><Camera className="w-4 h-4 mr-2" /> Take Verification Selfie</>
        )}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={handleVerify}
        className="hidden"
      />
    </div>
  );
}
